import { css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import "ui-components/button/wow-button.ts";
import "ui-components/divider";
import "ui-components/form-elements/input/wow-input.ts";
import "ui-components/wow-logo/wow-logo";

import { isValidPassword } from "helpers/password";
import { WowFormController } from "ui-common";
import { SignalElement, inject } from "ui-common/di";
import type { IRpcService } from "ui-common/services/rpc/types";
import { NotificationService } from "ui-components/notification";
import { t, translate } from "../translations/i18n";

const styles = css`
  :host {
    display: block;
    min-height: 100dvh;
    background-color: var(--sds-color-background-default-default);
    color: var(--sds-color-text-default-default);
    font-family: var(--sds-typography-family-body);
  }

  /* Base layout utilities */
  .container,
  .main-content,
  .header,
  .title-section,
  .form-section,
  .footer {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }

  .container {
    width: 100%;
    max-width: 393px;
    margin: 0 auto;
    padding: var(--sds-size-space-800);
    min-height: 100dvh;
  }

  .logo {
    margin-bottom: var(--sds-size-space-1600);
  }

  .main-content {
    gap: var(--sds-size-space-800);
  }

  .header,
  .title-section {
    gap: var(--sds-size-space-800);
  }

  .title-section {
    gap: var(--sds-size-space-400);
  }

  .title {
    font: var(--sds-typography-weight-medium) var(--sds-typography-scale-06) / 1.2 var(--sds-typography-family-title);
  }

  .subtitle {
    font-family: var(--sds-typography-family-body);
    font-weight: var(--sds-typography-weight-regular);
    font-size: var(--sds-typography-scale-03);
    line-height: 1.4;
    color: var(--sds-color-text-default-secondary);
  }

  .form-section {
    gap: var(--sds-size-space-600);
  }

  .submit-button {
    display: grid;
  }

  .form-error {
    color: var(--sds-color-text-danger-default);
    font-size: var(--sds-typography-scale-02);
    margin-top: var(--sds-size-space-300);
    text-align: center;
  }

  .form-success {
    color: var(--sds-color-text-success-default);
    font-size: var(--sds-typography-scale-02);
    margin-top: var(--sds-size-space-300);
    text-align: center;
  }

  form {
    display: contents;
  }

  wow-divider {
    margin-top: var(--sds-size-space-300);
  }
`;

interface ChangePasswordFormData {
  currentPassword: string;
  password: string;
  confirmPassword: string;
}

@customElement("change-password-page")
export class ChangePasswordPage extends SignalElement {
  static override styles = [styles];

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @state()
  private formError = "";

  @state()
  private formSuccess = "";

  @state()
  private isSubmitting = false;

  @state()
  private onboarding = false;

  override connectedCallback() {
    super.connectedCallback();
    this.extractUrlParams();
  }

  private formController: WowFormController<ChangePasswordFormData> = new WowFormController(
    this,
    {
      currentPassword: "",
      password: "",
      confirmPassword: "",
    },
    {
      alwaysValidateOnInput: false,
      validateOnBlur: true,
      validators: {
        currentPassword: [
          (value: string) => {
            if (!value) return translate("reset-password.passwordRequired");

            const { minLength, maxLength, hasNumber, hasUpperLowerCase } = isValidPassword(value);

            if (!minLength) return translate("reset-password.passwordMinLength");
            if (!maxLength) return translate("reset-password.passwordMaxLength");
            if (!hasNumber) return translate("reset-password.passwordNumber");
            if (!hasUpperLowerCase) return translate("reset-password.passwordCase");

            return;
          },
        ],
        password: [
          (value: string) => {
            if (!value) return translate("reset-password.passwordRequired");

            const { minLength, maxLength, hasNumber, hasUpperLowerCase } = isValidPassword(value);

            if (!minLength) return translate("reset-password.passwordMinLength");
            if (!maxLength) return translate("reset-password.passwordMaxLength");
            if (!hasNumber) return translate("reset-password.passwordNumber");
            if (!hasUpperLowerCase) return translate("reset-password.passwordCase");

            return;
          },
        ],
        confirmPassword: [
          (value: string) => {
            const password = this.formController.getValue("password") as string;

            if (!value) {
              return translate("reset-password.passwordRequired");
            }
            // eslint-disable-next-line security/detect-possible-timing-attacks
            if (value !== password) {
              return translate("reset-password.passwordMismatch");
            }
            return;
          },
        ],
      },
    },
    async (event) => {
      this.formError = "";
      this.formSuccess = "";

      if (!event || !event.data || !event.isValid) {
        return;
      }

      const { data, isValid } = event;

      if (!isValid) {
        this.formError = translate("reset-password.fixErrors");
        return;
      }

      // Check if passwords match
      if (data.password !== data.confirmPassword) {
        this.formError = translate("reset-password.passwordMismatch");
        return;
      }

      this.isSubmitting = true;

      try {
        const result = await this.rpcService.call("changePassword", {
          password: data.password,
          confirmPassword: data.confirmPassword,
          ...(this.onboarding && { onboarding: true }),
          ...(!this.onboarding && { currentPassword: data.currentPassword }),
        });

        if (result) {
          this.formSuccess = translate("change-password.success");
          NotificationService.success(translate("change-password.success"));

          window.open(`/feed`, "_self", "noopener,noreferrer");
        } else {
          this.formError = translate("reset-password.generalError");
        }
      } catch (error) {
        this.formError = translate("reset-password.generalError");
        console.error("Password change error:", error);
      } finally {
        this.isSubmitting = false;
      }
    },
  );

  private extractUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);
    this.onboarding = urlParams.get("onboarding") === "true";
  }

  private renderContent() {
    return html`
      <div class="header">
        <div class="title-section">
          <h1 class="title">${t("change-password.title")}</h1>
          <p class="subtitle">${t("reset-password.subtitle")}</p>
        </div>
      </div>

      <form id="change-password-form">
        <div class="form-section">
          ${this.onboarding
            ? nothing
            : html`
                <wow-input
                  name=${this.formController.fieldNames.currentPassword}
                  type="password"
                  placeholder=${t("change-password.currentPassword")}
                  autocomplete="new-password"
                  required
                  ?disabled=${this.isSubmitting}
                >
                </wow-input>
                <wow-divider simple></wow-divider>
              `}

          <wow-input
            name=${this.formController.fieldNames.password}
            type="password"
            placeholder=${t("reset-password.newPassword")}
            autocomplete="new-password"
            required
            ?disabled=${this.isSubmitting}
          >
          </wow-input>

          <wow-input
            name=${this.formController.fieldNames.confirmPassword}
            type="password"
            placeholder=${t("reset-password.confirmPassword")}
            autocomplete="new-password"
            required
            ?disabled=${this.isSubmitting}
          >
          </wow-input>

          ${this.formError ? html`<div class="form-error">${this.formError}</div>` : ""}
          ${this.formSuccess ? html`<div class="form-success">${this.formSuccess}</div>` : ""}

          <wow-button form="reset-password-form" class="submit-button" type="submit" ?disabled=${this.isSubmitting}>
            ${this.isSubmitting ? t("change-password.changing") : t("change-password.changePassword")}
          </wow-button>
        </div>
      </form>
    `;
  }

  protected override render() {
    return html`
      <div class="container">
        <div class="logo">
          <wow-logo-full></wow-logo-full>
        </div>

        <div class="main-content">${this.renderContent()}</div>
      </div>
    `;
  }
}
